import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Form,
  Message,
  Loader,
  Header,
  Icon,
  Dropdown,
  Input,
  Segment,
  Label,
  Grid,
} from "semantic-ui-react";
import { authAPI } from "../api";
import { showNotification } from "../utils/notifications";

const roleOptions = [
  { key: "user", value: "user", text: "Utente" },
  { key: "admin", value: "admin", text: "Admin" },
];

export default function GestioneUtenti({ open, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "user",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // carica lista di utenti quando si apre il modale
  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await authAPI.getUsers();
      setUsers(response.users || []);
    } catch (error) {
      showNotification(error.message || "Errore nel caricamento utenti", true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, { name, value }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // reset errore per questo campo
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };
  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = "L'Email è obbligatoria";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "L'Email non è valida";
    }

    if (!formData.password) {
      errors.password = "La password è obbligatoria, min. 8 caratteri";
    } else if (formData.password.length < 8) {
      errors.password = "Password deve essere di almeno 8 caratteri";
    }

    if (!formData.role) {
      errors.role = "Il ruolo è obbligatorio";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0; // se l'oggetto errors non ha proprietà significa che non ci sono errori
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;

    setIsCreating(true);

    try {
      await authAPI.createUser(
        formData.email,
        formData.password,
        formData.role
      );
      showNotification("Utente creato con successo!", false);

      // reset form
      setFormData({
        email: "",
        password: "",
        role: "user",
      });
      setShowPassword(false);
      setShowCreateForm(false);

      // ricarica la lista degli utenti
      loadUsers();
    } catch (error) {
      showNotification(error.message || "Errore nella creazione utente", true);
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      role: "user",
    });
    setFormErrors({});
    setShowPassword(false);
    setShowCreateForm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header>
        <Icon name="users" />
        Gestione Utenti
      </Modal.Header>

      <Modal.Content>
        {loading ? (
          <Loader active>Caricamento utenti...</Loader>
        ) : (
          <>
            <Segment>
              <Header as="h3">
                <Icon name="list" />
                Utenti Esistenti
              </Header>
              {users.length === 0 ? (
                <Message info>
                  <Message.Header>Nessun utente trovato</Message.Header>
                  <p>Non ci sono utenti registrati nel sistema.</p>
                </Message>
              ) : (
                <Table celled striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>ID</Table.HeaderCell>
                      <Table.HeaderCell>Email</Table.HeaderCell>
                      <Table.HeaderCell>Ruolo</Table.HeaderCell>
                      <Table.HeaderCell>Data Creazione</Table.HeaderCell>
                      <Table.HeaderCell>Ultimo Accesso</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {users.map((user) => (
                      <Table.Row key={user.id}>
                        <Table.Cell>{user.id}</Table.Cell>
                        <Table.Cell>{user.email}</Table.Cell>
                        <Table.Cell>
                          <Label color={user.role === "admin" ? "red" : "blue"}>
                            {user.role === "admin"
                              ? "Amministratore"
                              : "Utente"}
                          </Label>
                        </Table.Cell>
                        <Table.Cell>{formatDate(user.created_at)}</Table.Cell>
                        <Table.Cell>
                          {user.last_access
                            ? formatDate(user.last_access)
                            : "Mai"}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </Segment>

            {/* Pulsante per mostrare il form di creazione */}
            {!showCreateForm && (
              <Button
                primary
                icon="plus"
                content="Crea nuovo utente"
                onClick={() => setShowCreateForm(true)}
              />
            )}
            {/* Form creazione utente */}
            {showCreateForm && (
              <Segment>
                <Header as="h3">
                  <Icon name="user plus" />
                  Crea nuovo utente
                </Header>
                <Form error={Object.keys(formErrors).length > 0}>
                  <Grid columns={2} stackable>
                    <Grid.Column>
                      <Form.Field error={!!formErrors.email}>
                        <label>Email</label>
                        <Input
                          name="email"
                          type="email"
                          placeholder="utente@esempio.com"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                        {formErrors.email && (
                          <Label basic color="red" pointing>
                            {formErrors.email}
                          </Label>
                        )}
                      </Form.Field>

                      <Form.Field error={!!formErrors.role}>
                        <label>Ruolo</label>
                        <Dropdown
                          name="role"
                          placeholder="Seleziona ruolo"
                          fluid
                          selection
                          options={roleOptions}
                          value={formData.role}
                          onChange={handleInputChange}
                        />
                        {formErrors.role && (
                          <Label basic color="red" pointing>
                            {formErrors.role}
                          </Label>
                        )}
                      </Form.Field>
                    </Grid.Column>
                    <Grid.Column>
                      <Form.Field error={!!formErrors.password}>
                        <label>Password</label>
                        <Input
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Inserisci password"
                          value={formData.password}
                          onChange={handleInputChange}
                          action={{
                            icon: showPassword ? "eye slash" : "eye",
                            onClick: () => setShowPassword(!showPassword),
                          }}
                        />
                        {formErrors.password && (
                          <Label basic color="red" pointing>
                            {formErrors.password}
                          </Label>
                        )}
                      </Form.Field>
                    </Grid.Column>
                  </Grid>
                  {Object.keys(formErrors).length > 0 && (
                    <Message error>
                      <Message.Header>Errori nel form</Message.Header>
                      <Message.List>
                        {Object.values(formErrors).map((error, index) => (
                          <Message.Item key={index}>{error}</Message.Item>
                        ))}
                      </Message.List>
                    </Message>
                  )}
                  <Button.Group>
                    <Button
                      type="button"
                      onClick={resetForm}
                      disabled={isCreating}
                    >
                      Annulla
                    </Button>
                    <Button.Or text="o" />
                    <Button
                      positive
                      type="button"
                      onClick={handleCreateUser}
                      loading={isCreating}
                      disabled={isCreating}
                    >
                      Crea Utente
                    </Button>
                  </Button.Group>
                </Form>
              </Segment>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Chiudi</Button>
      </Modal.Actions>
    </Modal>
  );
}
